import { c as _c } from "react/compiler-runtime";
import { getIconFromSlug, metadata, type AITool } from "@app/_metadata";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";
import { FileUploadStatusPopover } from "@shared-components/file-upload-status-popover";
import { AboutContent } from "@shared-components/global/about-content";
import { DarkModeSwitch } from "@shared-components/global/dark-mode-switch";
import { topBarHelpLinks } from "@shared-components/global/help-links";
import { ReportBugForm } from "@shared-components/global/report-bug-form";
import { Button } from "@shared-components/shadcn/button";
import { Dialog, DialogContent, DialogTitle } from "@shared-components/shadcn/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@shared-components/shadcn/dropdown-menu";
import { api } from "@shared-server/api";
import { BETA_FLAG } from "@shared-utils/constants";
import { compilePath } from "@shared-utils/routing";
import { cn } from "@shared-utils/ui";
import { env } from "app/_shared/env.mjs";
import { AreaChart, BookOpenText, Bug, FileQuestion, GraduationCap, Menu, Settings } from "lucide-react";
import Link from "next/link";
import { forwardRef, type ComponentPropsWithoutRef, type ReactNode, useState } from "react";
interface TopBarMenuProps {
  activeAiTool: AITool;
}
interface TopBarMenuButtonProps extends ComponentPropsWithoutRef<typeof Button> {
  mobileIcon?: ReactNode;
}
export const TopBarMenuButton = forwardRef<HTMLButtonElement, TopBarMenuButtonProps>((t0, ref) => {
  const $ = _c(21);
  let children;
  let className;
  let mobileIcon;
  let props;
  if ($[0] !== t0) {
    ({
      children,
      className,
      mobileIcon,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = mobileIcon;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    mobileIcon = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn("group relative px-1.5 md:px-2 no-underline hover:no-underline hover:opacity-100", className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== mobileIcon) {
    t2 = mobileIcon ? <span className="md:hidden" aria-hidden="true">{mobileIcon}</span> : null;
    $[7] = mobileIcon;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const t3 = !!mobileIcon;
  let t4;
  if ($[9] !== t3) {
    t4 = cn("relative font-heading font-medium", {
      "hidden md:inline": t3
    });
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="absolute bottom-0 left-0 h-[2px] w-0 bg-sec-bright-green transition-all duration-300 ease-out group-hover:w-full" />;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== children || $[13] !== t4) {
    t6 = <span className={t4}>{children}{t5}</span>;
    $[12] = children;
    $[13] = t4;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== props || $[16] !== ref || $[17] !== t1 || $[18] !== t2 || $[19] !== t6) {
    t7 = <Button ref={ref} className={t1} variant="text" {...props}>{t2}{t6}</Button>;
    $[15] = props;
    $[16] = ref;
    $[17] = t1;
    $[18] = t2;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
});
TopBarMenuButton.displayName = "TopBarMenuButton";
export const TopBarMenu = props => {
  const $ = _c(58);
  const [showAbout, setShowAbout] = useState(false);
  const [showReportBug, setShowReportBug] = useState(false);
  const isAdminQuery = api.admin.isAdmin.useQuery();
  const isAdmin = !isAdminQuery.isLoading && isAdminQuery.data;
  const activeFeatureFlags = api.feature.allEnabledForMe.useQuery();
  let T0;
  let T1;
  let monitorUrl;
  let t0;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== activeFeatureFlags.data) {
    const hasBetaFlag = activeFeatureFlags.data?.includes(BETA_FLAG);
    monitorUrl = "";
    if (env.NEXT_PUBLIC_ENV) {
      monitorUrl = `https://leo-pharma.sentry.io/projects/hub-${env.NEXT_PUBLIC_ENV}/?project=4509213606805584`;
    }
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <FileUploadStatusPopover />;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t9 = <TopBarMenuButton aria-label="Bug report" mobileIcon={<Bug size={18} strokeWidth={1.75} />} onClick={() => setShowReportBug(true)}>{"Bug report"}</TopBarMenuButton>;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    T1 = DropdownMenu;
    t6 = false;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <DropdownMenuTrigger asChild={true}><TopBarMenuButton aria-label="Menu" mobileIcon={<Menu size={18} strokeWidth={1.75} />}>{"Menu"}</TopBarMenuButton></DropdownMenuTrigger>;
      $[16] = t7;
    } else {
      t7 = $[16];
    }
    T0 = DropdownMenuContent;
    t0 = "w-56";
    t1 = "bottom";
    t2 = "end";
    t3 = 15;
    if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <DropdownMenuLabel className="text-sm font-bold">{"Tools"}</DropdownMenuLabel>;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    t5 = metadata.aiTools?.filter(_temp).filter(appConfig => !appConfig.requiredFeatureFlags || hasBetaFlag || appConfig.requiredFeatureFlags.every(flag => activeFeatureFlags.data?.includes(flag))).map(_temp2);
    $[0] = activeFeatureFlags.data;
    $[1] = T0;
    $[2] = T1;
    $[3] = monitorUrl;
    $[4] = t0;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
    $[13] = t9;
  } else {
    T0 = $[1];
    T1 = $[2];
    monitorUrl = $[3];
    t0 = $[4];
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
    t5 = $[9];
    t6 = $[10];
    t7 = $[11];
    t8 = $[12];
    t9 = $[13];
  }
  let t10;
  let t11;
  let t12;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <DropdownMenuSeparator />;
    t11 = <DropdownMenuLabel className="text-sm font-bold">{"Learn"}</DropdownMenuLabel>;
    t12 = topBarHelpLinks.map(_temp3);
    $[18] = t10;
    $[19] = t11;
    $[20] = t12;
  } else {
    t10 = $[18];
    t11 = $[19];
    t12 = $[20];
  }
  let t13;
  if ($[21] !== isAdmin || $[22] !== monitorUrl) {
    t13 = isAdmin && <><DropdownMenuSeparator /><DropdownMenuLabel className="text-sm font-bold">{"Admin"}</DropdownMenuLabel><DropdownMenuItem asChild={true}><a href="/docs"><BookOpenText className="mr-2" strokeWidth={1.5} size={16} />{"Docs"}</a></DropdownMenuItem>{monitorUrl && <DropdownMenuItem asChild={true}><a href={monitorUrl}><AreaChart className="mr-2" strokeWidth={1.5} size={16} />{"Sentry Monitoring"}</a></DropdownMenuItem>}<DropdownMenuItem asChild={true}><a href="/admin/feature-flags"><Settings className="mr-2" strokeWidth={1.5} size={16} />{"Admin"}</a></DropdownMenuItem></>;
    $[21] = isAdmin;
    $[22] = monitorUrl;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <DropdownMenuSeparator />;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  let t16;
  let t17;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <DropdownMenuItem onClick={() => setShowAbout(true)}>{"About"}</DropdownMenuItem>;
    t16 = <DropdownMenuSeparator />;
    t17 = <DarkModeSwitch />;
    $[25] = t15;
    $[26] = t16;
    $[27] = t17;
  } else {
    t15 = $[25];
    t16 = $[26];
    t17 = $[27];
  }
  let t18;
  if ($[28] !== T0 || $[29] !== t0 || $[30] !== t1 || $[31] !== t13 || $[32] !== t2 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5) {
    t18 = <T0 className={t0} side={t1} align={t2} sideOffset={t3}>{t4}{t5}{t10}{t11}{t12}{t13}{t14}{t15}{t16}{t17}</T0>;
    $[28] = T0;
    $[29] = t0;
    $[30] = t1;
    $[31] = t13;
    $[32] = t2;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== T1 || $[38] !== t18 || $[39] !== t6 || $[40] !== t7) {
    t19 = <T1 modal={t6}>{t7}{t18}</T1>;
    $[37] = T1;
    $[38] = t18;
    $[39] = t6;
    $[40] = t7;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = open => setShowAbout(open);
    $[42] = t20;
  } else {
    t20 = $[42];
  }
  let t21;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <VisuallyHidden.Root><DialogTitle>About</DialogTitle></VisuallyHidden.Root>;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  let t22;
  if ($[44] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = () => setShowAbout(false);
    $[44] = t22;
  } else {
    t22 = $[44];
  }
  let t23;
  if ($[45] !== props.activeAiTool) {
    t23 = <DialogContent className="p-0">{t21}<AboutContent onClose={t22} aiTool={props.activeAiTool} /></DialogContent>;
    $[45] = props.activeAiTool;
    $[46] = t23;
  } else {
    t23 = $[46];
  }
  let t24;
  if ($[47] !== showAbout || $[48] !== t23) {
    t24 = <Dialog open={showAbout} onOpenChange={t20}>{t23}</Dialog>;
    $[47] = showAbout;
    $[48] = t23;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  let t25;
  if ($[50] !== showReportBug) {
    t25 = <ReportBugForm setOpen={setShowReportBug} open={showReportBug} />;
    $[50] = showReportBug;
    $[51] = t25;
  } else {
    t25 = $[51];
  }
  let t26;
  if ($[52] !== t19 || $[53] !== t24 || $[54] !== t25 || $[55] !== t8 || $[56] !== t9) {
    t26 = <>{t8}{t9}{t19}{t24}{t25}</>;
    $[52] = t19;
    $[53] = t24;
    $[54] = t25;
    $[55] = t8;
    $[56] = t9;
    $[57] = t26;
  } else {
    t26 = $[57];
  }
  return t26;
};
function _temp(aiTool) {
  return aiTool.tool;
}
function _temp2(aiTool_0) {
  return <DropdownMenuItem asChild={true} key={aiTool_0.slug}><Link href={compilePath(aiTool_0.slug)}><div className={cn(aiTool_0.meta.strokeColor, "flex h-6 w-6 items-center justify-center rounded-sm")}>{getIconFromSlug(aiTool_0.slug, {
          strokeWidth: 1.5,
          size: 20,
          className: aiTool_0.meta.strokeColor
        })}</div><span className="ml-2 font-heading text-lg">{aiTool_0.meta.name}</span></Link></DropdownMenuItem>;
}
function _temp3(link) {
  return <DropdownMenuItem asChild={true} key={link.label}><a target="_blank" rel="noreferrer" href={link.href}>{link.key === "academy" ? <GraduationCap className="mr-2" strokeWidth={1.5} size={16} /> : <FileQuestion className="mr-2" strokeWidth={1.5} size={16} />}{link.label}</a></DropdownMenuItem>;
}